import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import path from 'path';
import { cssGenerator } from '@/mixins/css-generator';
import { youtube } from '@/mixins/youtube';
import tooltip from '@/mixins/components/tooltip';
import { SQUASH_DROPLET } from '@/constants';
import AudioPlayerTemp from '@editor/droplets/audio-player/components/player';
import VideoPlayerTemp from '@editor/droplets/video-player/components/player';
import DropletOptions from '../_bubble-options';
import MediaItemOptions from './components/_edit-options';
import schema from './droplet.schema';
const dropletSchema = schema();

export default Vue.component('media-item', {
  template: require('./template.html'),
  name: 'media-item',
  mixins: [cssGenerator, youtube],
  computed: {
    ...mapGetters([
      'currentSite',
      'currentPage',
      'viewport',
      'localStyle',
      'activeDroplet',
      'activeState',
      'animationPreview',
      'dropletObserver',
      'dragging',
      'globalBubbleOptionsOpen',
    ]),
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    isActiveDroplet() {
      return this.activeDroplet._id === this.droplet._id;
    },
    hideTransform() {
      return this.dragging || this.globalBubbleOptionsOpen;
    },
    mediaItem() {
      const enabled = this.currentSite.dynamic && this.currentSite.dynamic.media;
      const items = enabled ? (enabled.published || []) : [];
      const ref = this.droplet.item;
      const item = ref && enabled ? _.find(items, p => p._id === ref) : false;
      return item ||
      {
        title: 'No Media Linked',
        thumbnail: `${process.env.S3_ASSETS_URL}mock-images/default-images/mock-media.svg`,
        media: `${process.env.S3_ASSETS_URL}multi-media/sample-audio.mp3`,
        externalMedia: {},
        summary: 'No summary provided.',
        released: {
          date: false,
          month: false,
          day: false,
          year: false,
        },
        categories: [],
        groups: [],
        created: new Date().toISOString(),
        publishedOn: new Date().toISOString(),
        _placeholder: true,
      };
    },
    checks() {
      const item = this.mediaItem;
      const isAnyVideo = (item.media && this.mediaType === 'video') || (!item.media && item.externalMedia.youtube);
      const isUploadedAudio = (item.media && this.mediaType === 'audio') || (!item.media && !item.externalMedia.youtube);
      const isUploadedVideo = item.media && this.mediaType === 'video';
      const isYouTubeFallback = !item.media && item.externalMedia.youtube;
      const noDisplay = !item.media && !item.externalMedia.youtube;
      this.$store.dispatch('setStyleCondition', {
        _id: this.droplet._id,
        condition: 'isAudioPlayer',
        value: isUploadedAudio,
      });
      this.$store.dispatch('setStyleCondition', {
        _id: this.droplet._id,
        condition: 'isVideoPlayer',
        value: isUploadedVideo,
      });
      return {
        isAnyVideo,
        isUploadedAudio,
        isUploadedVideo,
        isYouTubeFallback,
        noDisplay,
      };
    },
    mediaType() {
      const file = path.extname(this.mediaItem.media);
      const audio = ['.mp3', '.wmv', '.wav'];
      const video = ['.mp4', '.mov', '.mpeg', '.mpg'];
      let type;
      if (audio.includes(file)) {
        type = 'audio';
      } else if (video.includes(file)) {
        type = 'video';
      }
      return type;
    },
    inGroup() {
      const value = this.joinedGroups.length > 0;
      this.$store.dispatch('setStyleCondition', {
        _id: this.droplet._id,
        condition: 'inGroup',
        value,
      });
      return value;
    },
    joinedGroups() {
      const all = this._currentDynamic('media').groups || [];
      return _.filter(all, g => this.mediaItem.groups.includes(g.name) && g.items.length > 1);
    },
    profile() {
      return this._dropletType(this.droplet);
    },
    displayStatus() {
      let settings = {
        desktop: 'default',
        tablet: 'default',
        mobile: 'default',
      };
      const selector = _.find(this.droplet.mediaQueries, { selector: ', .drzMedia-item' });
      if (selector) {
        const property = _.find(selector.properties, { property: 'display' });
        if (property) {
          settings = property;
        }
      }
      return settings;
    },
  },
  data() {
    return {
      type: dropletSchema.type,
      SQUASH_DROPLET,
      display: {
        top: null,
        left: null,
        right: null,
        bottom: null,
        ctPaddingTop: null,
        ctPaddingBottom: null,
        ctPaddingLeft: null,
        ctPaddingRight: null,
        transform: null,
        perspective: null,
        titleSize: null,
        titleLine: null,
        titleLetterSpacing: null,
        pubSize: null,
        pubLine: null,
        pubLetterSpacing: null,
        linkSize: null,
        linkLine: null,
        linkLetterSpacing: null,
        linkWidthBottom: null,
        groupDivBrdWidth: null,
        groupDivRadius: null,
        groupDivWidth: null,
        grpWrapPaddingTop: null,
        grpWrapPaddingBottom: null,
        grpWrapPaddingRight: null,
        grpWrapPaddingLeft: null,
        grpItemBrdRightWidth: null,
        grpItemBrdLeftWidth: null,
        grpItemBrdTopWidth: null,
        grpItemBrdBottomWidth: null,
        grpItemBrdTopRightRadius: null,
        grpItemBrdTopLeftRadius: null,
        grpItemBrdBottomRightRadius: null,
        grpItemBrdBottomLeftRadius: null,
        titleItemLine: null,
        pubItemLine: null,
        'box-shadow': null,
      },
      hidden: false,
      dropletHovered: false,
      setBubbleSize: 'dropletBubble-normal',
      showEditBubble: false,
      showRevealClass: false,
      externalText: {
        youtube: 'Watch on YouTube',
        vimeo: 'Watch on Vimeo',
        twitch: 'Watch on Twitch',
        facebook: 'Watch on Facebook',
        soundcloud: 'Liston on Soundcloud',
        spotify: 'Listen on Spotify',
        other: 'Open Other Link',
      },
    };
  },
  props: ['droplet', 'items', 'index'],
  components: {
    DropletOptions,
    MediaItemOptions,
    AudioPlayerTemp,
    VideoPlayerTemp,
    tooltip,
  },
  watch: {
    isActiveDroplet(is) {
      if (is) {
        this._scrollToDroplet(this.$el);
      }
    },
    'activeDroplet._id': function c(id) {
      if (!id) {
        this.showRevealClass = false;
      }
    },
    activeState(state) {
      if (state === ':reveal' && this.isActiveDroplet) {
        this.showRevealClass = true;
      } else {
        this.showRevealClass = false;
      }
    },
    animationPreview(should) {
      if (should && this.isActiveDroplet) {
        const droplet = this.$refs.dropletRow;
        const $droplet = $(droplet);
        $droplet.drzOnReveal({
          editing: this.isActiveDroplet,
          child: '.drzMedia-item',
        });
      }
    },
    viewport() {
      this.callCssGenerator();
      this.observeVisibility();
    },
    'localStyle.value': function setLocals() {
      this.setDisplay();
    },
    dropletObserver(id) {
      if (this.droplet._id === id) {
        this.observeVisibility();
        this.$store.dispatch('setDropletObserver', false);
      }
    },
  },
  mounted() {
    this.callCssGenerator();
    this.observeVisibility();
  },
  methods: {
    callCssGenerator() {
      return this.generateCSS({
        drp: this.droplet,
      });
    },
    observeVisibility() {
      const display = this.displayStatus;
      if (display[this.viewport] === 'none' || (display[this.viewport] === 'default' && display.desktop === 'none')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
    },
    setDisplay() {
      if (this.activeDroplet._id === this.droplet._id) {
        const styles = this.localStyle;
        if (styles.properties.length > 0) {
          _.forEach(styles.properties, (property) => {
            this.$set(this.display, property, styles.value);
          });
        } else {
          _.forOwn(this.display, (value, key) => {
            this.display[key] = null;
          });
        }
      }
    },
    toggleBubble() {
      if (!this.roamingTemplate) {
        this.showEditBubble = !this.showEditBubble;
      }
    },
    groupItems(items) {
      const published = this._currentDynamic('media').published || [];
      return items.map(_id => _.find(published, p => p._id === _id));
    },
    bindPlugin() {
      const $player = $(this.$refs.drzAudio);
      $player.drzAudioPlayer();
    },
    unBindPlugin() {
      const ref = this.$refs.drzAudio;
      const $player = $(ref);
      if (ref) {
        $player.drzAudioPlayer.destroy($player);
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/media-item/index.js